import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AppLink, useGo } from '../components/Nav';
import { VideosTable } from '../components/VideosTable';
import { Fact, Facts, Identity, LoadMore, Mono, Rows, SearchBox, Section, Table, Td, Th, Tr } from '../components/data';
import { Badge, ErrorState, Loading, PageHeader } from '../components/ui';
import { errorMessage } from '../lib/api';
import { shortDate, timeAgo } from '../lib/format';
import { useDebounced } from '../lib/useDebounced';
import { fetchUser, fetchUsers, qk } from '../queries/admin';

export function UsersPage() {
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim());
  const go = useGo();

  const users = useInfiniteQuery({
    queryKey: qk.users(query),
    queryFn: ({ pageParam }) => fetchUsers(query, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const rows = users.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader title="Users" subtitle="Everyone with a Slate account, newest first." />
      <div className="mb-4">
        <SearchBox value={q} onChange={setQ} placeholder="Search by email or name" busy={users.isFetching && !users.isFetchingNextPage} />
      </div>

      <Table>
        <thead>
          <tr>
            <Th>Person</Th>
            <Th>Teams</Th>
            <Th align="right">Recordings</Th>
            <Th align="right">Joined</Th>
          </tr>
        </thead>
        <Rows query={users} columns={4} empty={query ? `Nobody matches "${query}".` : 'No users yet.'}>
          {rows.map((user) => (
            <Tr key={user.id} onClick={() => go('/users/$id', { params: { id: user.id } })}>
              <Td className="max-w-[20rem]">
                <Identity name={user.name} email={user.email} avatarUrl={user.avatarUrl} />
              </Td>
              <Td>
                <span className="flex flex-wrap gap-1">
                  {user.memberships.length === 0 ? (
                    <span className="text-chalk-faint">none</span>
                  ) : (
                    user.memberships.map((membership) => (
                      <Badge key={membership.org.id} tone={membership.seat === 'paid' ? 'info' : 'neutral'}>
                        {membership.org.name} · {membership.role}
                      </Badge>
                    ))
                  )}
                </span>
              </Td>
              <Td align="right">{user._count.videos.toLocaleString()}</Td>
              <Td align="right" className="whitespace-nowrap text-chalk-dim" title={user.createdAt}>
                {timeAgo(user.createdAt)}
              </Td>
            </Tr>
          ))}
        </Rows>
      </Table>
      <LoadMore query={users} />
    </>
  );
}

export function UserPage({ id }: { id: string }) {
  const user = useQuery({ queryKey: qk.user(id), queryFn: () => fetchUser(id) });

  if (user.isPending) {
    return (
      <>
        <PageHeader title="User" />
        <Loading />
      </>
    );
  }
  if (user.isError) {
    return (
      <>
        <PageHeader title="User" />
        <ErrorState message={errorMessage(user.error)} onRetry={() => void user.refetch()} />
      </>
    );
  }

  const data = user.data;

  return (
    <>
      <p className="mb-3 text-caption text-chalk-faint">
        <AppLink to="/users" className="hover:text-chalk-dim">
          Users
        </AppLink>{' '}
        /
      </p>
      <PageHeader title={data.name?.trim() || data.email} subtitle={data.name ? data.email : undefined} />

      <Facts>
        <Fact label="Joined">
          {shortDate(data.createdAt)} <span className="text-chalk-faint">({timeAgo(data.createdAt)})</span>
        </Fact>
        <Fact label="Recordings">{data._count.videos.toLocaleString()}</Fact>
        <Fact label="Comments">{data._count.comments.toLocaleString()}</Fact>
        <Fact label="Request links">{data._count.requestLinks.toLocaleString()}</Fact>
        <Fact label="Desktop sessions">{data._count.deviceSessions.toLocaleString()}</Fact>
        <Fact label="Firebase uid">
          <Mono>{data.firebaseUid}</Mono>
        </Fact>
        <Fact label="User id">
          <Mono>{data.id}</Mono>
        </Fact>
      </Facts>

      <Section title="Teams" count={data.memberships.length}>
        <Table>
          <thead>
            <tr>
              <Th>Team</Th>
              <Th>Role</Th>
              <Th>Seat</Th>
            </tr>
          </thead>
          <tbody>
            {data.memberships.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-8 text-center text-body text-chalk-faint">
                  Not on any team. Personal library only.
                </td>
              </tr>
            ) : (
              data.memberships.map((membership) => (
                <Tr key={membership.org.id}>
                  <Td>
                    <AppLink to="/teams/$id" params={{ id: membership.org.id }} className="text-chalk hover:underline">
                      {membership.org.name}
                    </AppLink>
                  </Td>
                  <Td className="capitalize">{membership.role}</Td>
                  <Td>
                    <Badge tone={membership.seat === 'paid' ? 'info' : 'neutral'}>{membership.seat}</Badge>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </Section>

      <Section title="Recent recordings" count={data._count.videos}>
        <VideosTable videos={data.videos} showOwner={false} empty="Nothing recorded yet." />
      </Section>
    </>
  );
}
